import React from "react";
import { TrendingUp } from "lucide-react";

const SHORT_LABELS = {
  rim: "RIM",
  mid_paint: "MID PAINT",
  mid_range: "MIDDY",
  three_cs_unguarded: "C+S 3\nUNCON",
  three_cs_guarded: "C+S 3\nCON",
  three_off_dribble: "OD 3",
};
const ORDER = ["rim", "mid_paint", "mid_range", "three_cs_unguarded", "three_cs_guarded", "three_off_dribble"];
const GOLD = "#d8b854";
const GREEN = "#16a34a";
const RED = "#dc2626";

const ordinal = (n) => {
  if (!n) return { n: "—", sfx: "" };
  const v = n % 100;
  const sfx = v >= 11 && v <= 13 ? "TH" : ["TH", "ST", "ND", "RD"][n % 10] || "TH";
  return { n, sfx };
};

const fmtUse = (u) => (u === null || u === undefined ? "—" : `${Math.round(u * 100)}%`);
const fmtPps = (p) => {
  if (p === null || p === undefined) return "—";
  const s = Number(p).toFixed(2);
  return s.startsWith("0") ? s.slice(1) : s;
};

function Rank({ rank, align, light }) {
  const { n, sfx } = ordinal(rank);
  return (
    <div style={{ width: 54, display: "flex", alignItems: "baseline", justifyContent: align === "right" ? "flex-start" : "flex-end", gap: 1, fontFamily: "'Bebas Neue', Impact, sans-serif", color: light ? "#111111" : "#ffffff", textShadow: light ? `2px 2px 0 ${GOLD}` : "2px 2px 0 rgba(216,184,84,0.55)", lineHeight: 1 }}>
      <span style={{ fontSize: 34 }}>{n}</span>
      <span style={{ fontSize: 13, fontWeight: 700 }}>{sfx}</span>
    </div>
  );
}

function VsAvgIcon({ vs }) {
  if (vs === null || vs === undefined) return null;
  const up = vs >= 0;
  return (
    <span title={`${up ? "Above" : "Below"} average PPS (${up ? "+" : ""}${Number(vs).toFixed(2)})`}
      style={{ position: "absolute", right: 6, top: 6, width: 18, height: 18, borderRadius: 999, background: up ? GREEN : RED, color: "#fff", fontSize: 15, lineHeight: "18px", textAlign: "center", fontFamily: "Helvetica, Arial, sans-serif", fontWeight: 900 }}>
      {up ? "+" : "−"}
    </span>
  );
}

function IncreaseTag({ active, light, onClick, canEdit, testId }) {
  if (!active && (!canEdit || !onClick)) return <div />;
  const base = { display: "inline-flex", alignItems: "center", gap: 3, fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 12, letterSpacing: 1.5, padding: "3px 6px", lineHeight: 1, border: "1px solid", cursor: onClick && canEdit ? "pointer" : "default", transition: "background-color .15s, color .15s, border-color .15s" };
  const style = active
    ? { ...base, background: GREEN, borderColor: GREEN, color: "#fff" }
    : { ...base, background: "transparent", borderColor: light ? "#9ca3af" : "rgba(255,255,255,0.25)", color: light ? "#6b7280" : "#8a8a8a" };
  return (
    <button type="button" data-testid={testId} disabled={!canEdit || !onClick} onClick={onClick} style={style} aria-pressed={active}>
      <TrendingUp style={{ width: 10, height: 10 }} />
      INCREASE
    </button>
  );
}

function Circled({ children, on, light }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "6px 18px" }}>
      {on && (
        <svg viewBox="0 0 200 90" preserveAspectRatio="none" style={{ position: "absolute", inset: -4, width: "calc(100% + 8px)", height: "calc(100% + 8px)", pointerEvents: "none" }}>
          <ellipse cx="100" cy="45" rx="94" ry="38" fill="none" stroke={GOLD} strokeWidth="4" transform="rotate(-4 100 45)" />
          <ellipse cx="102" cy="47" rx="90" ry="34" fill="none" stroke={GOLD} strokeWidth="2.5" opacity="0.8" transform="rotate(3 100 45)" />
        </svg>
      )}
      <span style={{ position: "relative" }}>{children}</span>
    </span>
  );
}

const TR_METRICS = [
  { key: "ppp", label: "PPP", fmt: (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(v >= 1 ? 2 : 3).replace(/^0/, "")) },
  { key: "fg2_pct", label: "2FG%", fmt: (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`) },
  { key: "fg3_pct", label: "3FG%", fmt: (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`) },
  { key: "ft_rate", label: "FTR", fmt: (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2).replace(/^0/, "")) },
];

export function TransitionCompare({ rows, light = false, testId = "sp-transition-graphic" }) {
  const byLabel = Object.fromEntries((rows || []).map((r) => [r.label.toLowerCase().replace(/\s+/g, ""), r]));
  const tr = byLabel.transition || rows?.[0] || {};
  const hc = byLabel.halfcourt || rows?.[1] || {};
  const ink = light ? "#111111" : "#232323";
  const txt = light ? "#111111" : "#ffffff";
  const cols = "1fr 110px 1fr";
  const fmtUseLabel = (v) => (v === null || v === undefined ? null : `${Math.round(Number(v))}% USE`);
  return (
    <div data-testid={testId} style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", maxWidth: 560, margin: "0 auto", width: "100%", color: txt }}>
      <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", marginBottom: 10, fontStyle: "italic", letterSpacing: 2 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 26, fontWeight: 700 }}>TRANSITION</div>
          {fmtUseLabel(tr.use_pct) && <div style={{ fontSize: 13, color: GOLD, letterSpacing: 2 }} data-testid={`${testId}-tr-use`}>{fmtUseLabel(tr.use_pct)}</div>}
        </div>
        <div style={{ textAlign: "center", fontSize: 18 }}>VS</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 26, fontWeight: 700 }}>HALFCOURT</div>
          {fmtUseLabel(hc.use_pct) && <div style={{ fontSize: 13, color: GOLD, letterSpacing: 2 }} data-testid={`${testId}-hc-use`}>{fmtUseLabel(hc.use_pct)}</div>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {TR_METRICS.map((m) => {
          const a = tr[m.key], b = hc[m.key];
          const has = (v) => v !== null && v !== undefined;
          const trWins = has(a) && has(b) && Number(a) > Number(b);
          const hcWins = has(a) && has(b) && Number(b) > Number(a);
          return (
            <div key={m.key} data-testid={`${testId}-row-${m.key}`} style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center" }}>
              <div style={{ textAlign: "center", fontSize: 38, lineHeight: 1 }}><Circled on={trWins} light={light}>{m.fmt(a)}</Circled></div>
              <div style={{ background: ink, height: 54, display: "flex", alignItems: "center", justifyContent: "center", color: "#ffffff", fontSize: 20, letterSpacing: 2, fontStyle: "italic", boxShadow: light ? "0 0 0 2px #111111" : "0 0 0 2px #3a3a3a" }}>{m.label}</div>
              <div style={{ textAlign: "center", fontSize: 38, lineHeight: 1 }}><Circled on={hcWins} light={light}>{m.fmt(b)}</Circled></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ProfileBars({ title, leftHeader = "USE", rightHeader = "PPP", rows, light = false, testId = "sp-bars" }) {
  if (!rows || rows.length === 0) return null;
  const ink = light ? "#111111" : "#232323";
  const headColor = light ? "#111111" : "#ffffff";
  const subColor = light ? "#4b5563" : "#a0a0a0";
  const cols = "54px 1fr 118px 1fr 54px";
  return (
    <div data-testid={testId} style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", maxWidth: 560, margin: "0 auto", width: "100%" }}>
      {title && <div style={{ textAlign: "center", fontSize: 26, letterSpacing: 2, color: headColor, fontStyle: "italic", fontWeight: 700, marginBottom: 6 }}>{title}</div>}
      <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", marginBottom: 6, fontSize: 18, letterSpacing: 2, color: headColor, fontStyle: "italic" }}>
        <div />
        <div style={{ textAlign: "center" }}>{leftHeader}</div>
        <div />
        <div style={{ textAlign: "center" }}>{rightHeader}</div>
        <div />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r, i) => (
          <div key={r.key || i} data-testid={`${testId}-row-${i}`}>
            <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center" }}>
              <Rank rank={r.leftRank} align="left" light={light} />
              <div style={{ background: GOLD, height: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: "#111111", letterSpacing: 1 }}>{r.left}</div>
              <div style={{ background: ink, height: 58, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#ffffff", fontSize: r.label.length > 10 ? 14 : 17, letterSpacing: 1.5, lineHeight: 1.05, padding: "0 6px", boxShadow: light ? "0 0 0 2px #111111" : "0 0 0 2px #3a3a3a", textTransform: "uppercase", overflowWrap: "anywhere" }}>{r.label}</div>
              <div style={{ background: GOLD, height: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: "#111111", letterSpacing: 1 }}>{r.right}</div>
              <Rank rank={r.rightRank} align="right" light={light} />
            </div>
            {r.sub && r.sub.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: cols, marginTop: 4 }}>
                <div />
                <div style={{ gridColumn: "2 / 5", display: "flex", justifyContent: "center", gap: 18, fontSize: 15, letterSpacing: 1.5, color: subColor }}>
                  {r.sub.map((s) => (
                    <span key={s.label}><span style={{ opacity: 0.7 }}>{s.label}</span> <span style={{ color: headColor }}>{s.value}</span></span>
                  ))}
                </div>
                <div />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShotProfileGraphic({ categories, flags, canEdit = false, onToggleIncrease, light = false, testId = "sd-shot-profile" }) {
  const byKey = Object.fromEntries((categories || []).map((c) => [c.key, c]));
  const rows = ORDER.map((k) => byKey[k]).filter(Boolean);
  if (rows.length === 0) return null;
  const ink = light ? "#111111" : "#232323";
  const headColor = light ? "#111111" : "#ffffff";
  const cols = "54px 1fr 118px 1fr 54px";
  const showTags = canEdit || Object.values(flags || {}).some((f) => f && (f.use || f.pps));
  return (
    <div data-testid={testId} style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", maxWidth: 560, margin: "0 auto", width: "100%" }}>
      <div style={{ textAlign: "center", fontSize: 26, letterSpacing: 2, color: headColor, fontStyle: "italic", fontWeight: 700, marginBottom: 6 }}>SHOT PROFILE</div>
      <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", marginBottom: 6, fontSize: 18, letterSpacing: 2, color: headColor, fontStyle: "italic" }}>
        <div />
        <div style={{ textAlign: "center" }}>USE</div>
        <div />
        <div style={{ textAlign: "center" }}>PPS</div>
        <div />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: showTags ? 6 : 10 }}>
        {rows.map((c) => {
          const dim = c.rating === "insufficient";
          const f = (flags || {})[c.key] || {};
          return (
            <div key={c.key} data-testid={`${testId}-row-${c.key}`} style={{ opacity: dim ? 0.45 : 1 }}>
              <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center" }}>
                <Rank rank={c.usage_rank} align="left" light={light} />
                <div style={{ position: "relative", background: GOLD, height: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: "#111111", letterSpacing: 1, boxShadow: f.use ? `inset 0 0 0 3px ${GREEN}` : "none" }}>{fmtUse(c.usage)}</div>
                <div style={{ background: ink, height: 58, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#ffffff", fontSize: 17, letterSpacing: 1.5, lineHeight: 1.05, whiteSpace: "pre-line", padding: "0 6px", boxShadow: light ? "0 0 0 2px #111111" : "0 0 0 2px #3a3a3a" }}>{SHORT_LABELS[c.key]}</div>
                <div style={{ position: "relative", background: GOLD, height: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: "#111111", letterSpacing: 1, boxShadow: f.pps ? `inset 0 0 0 3px ${GREEN}` : "none" }}>
                  {fmtPps(c.pps)}
                  {!dim && <VsAvgIcon vs={c.vs_avg} />}
                </div>
                <Rank rank={c.pps_rank} align="right" light={light} />
              </div>
              {showTags && !dim && (
                <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", marginTop: 3 }}>
                  <div />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <IncreaseTag active={!!f.use} light={light} canEdit={canEdit} onClick={onToggleIncrease ? () => onToggleIncrease(c.key, "use") : undefined} testId={`${testId}-inc-use-${c.key}`} />
                  </div>
                  <div />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <IncreaseTag active={!!f.pps} light={light} canEdit={canEdit} onClick={onToggleIncrease ? () => onToggleIncrease(c.key, "pps") : undefined} testId={`${testId}-inc-pps-${c.key}`} />
                  </div>
                  <div />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
